import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Thin imperative Leaflet wrapper (no react-leaflet — avoids React-version coupling).
 * Uses circle markers so we never depend on Leaflet's default marker image assets.
 *
 * props:
 *  - markers: [{ id, lat, lng, color, label, popupHtml }]
 *  - polyline: [[lat,lng], ...]   (optional route trail)
 *  - center: [lat,lng]            (optional; auto-fits to markers/polyline otherwise)
 *  - zoom: number
 *  - height: css height (e.g. '200px')
 *  - onMarkerClick: (id) => void
 */
export default function LeafletMap({
  markers = [], polyline = null, center = null, zoom = 12, height = '420px',
  onMarkerClick, className = '',
}) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  // init once
  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: true })
      .setView(center || [20.5937, 78.9629], center ? zoom : 5);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '© OpenStreetMap',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // Leaflet needs a size recalculation once it is visible.
    setTimeout(() => map.invalidateSize(), 120);
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // redraw markers + polyline
  useEffect(() => {
    const map = mapRef.current;
    const grp = layerRef.current;
    if (!map || !grp) return;
    grp.clearLayers();
    const bounds = [];

    if (Array.isArray(polyline) && polyline.length > 1) {
      L.polyline(polyline, { color: '#3b4872', weight: 4, opacity: 0.85 }).addTo(grp);
      polyline.forEach((p) => bounds.push(p));
    }

    markers.forEach((m) => {
      if (m.lat == null || m.lng == null) return;
      const cm = L.circleMarker([m.lat, m.lng], {
        radius: 9, color: '#0f172a', weight: 2, fillColor: m.color || '#3b4872', fillOpacity: 0.95,
      }).addTo(grp);
      if (m.popupHtml) cm.bindPopup(m.popupHtml, { closeButton: true });
      if (m.label) cm.bindTooltip(m.label, { permanent: false, direction: 'top' });
      if (onMarkerClick) cm.on('click', () => onMarkerClick(m.id));
      bounds.push([m.lat, m.lng]);
    });

    if (center) {
      map.setView(center, zoom);
    } else if (bounds.length === 1) {
      map.setView(bounds[0], 14);
    } else if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
    setTimeout(() => map.invalidateSize(), 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(markers), JSON.stringify(polyline), JSON.stringify(center), zoom]);

  return <div ref={elRef} style={{ height }} className={`w-full rounded-sm overflow-hidden z-0 ${className}`} data-testid="leaflet-map" />;
}
